import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Task,
  Tag,
  User,
  Reminder,
  RoomStatus,
  TaskTemplate as BaseTaskTemplate,
  TemplateTaskDataDto,
  TemplateChildTaskDto,
} from "../types/types.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";

// Material-UI コンポーネントとアイコン
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import Switch from "@mui/material/Switch";
import NoteAltTwoToneIcon from "@mui/icons-material/NoteAltTwoTone";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentTurnedInTwoToneIcon from "@mui/icons-material/AssignmentTurnedInTwoTone";
import SubdirectoryArrowRightTwoToneIcon from "@mui/icons-material/SubdirectoryArrowRightTwoTone";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";

// ユーティリティ
// import { isoToYmd, ymdToUtcIso } from "../utils/date.ts";

// --- ★フェーズ2, 3 で追加 ---

interface TemplateEditorPanelProps {
  template: BaseTaskTemplate;
  onClose: () => void;
  roomMembers: User[];
  availableTags: Tag[];
  color: PaletteColor;
  honbuId: number;
  roomId: number;
  onTemplateUpdated: (templateId: number, newTaskData: string) => void;
}

// 内部で使う型 (一時キー付き)
type EditableTemplateChild = {
  _key: string;
  title: string;
  memo?: string;
  assigneeHonbuIds: number[];
  requesterHonbuIds: number[];
  tagIds: number[];
  startDateOffset: number | null;
  endDateOffset: number | null;
  childTasks: EditableTemplateChild[];
};

type EditableTemplateData = {
  title: string;
  memo?: string;
  notification: boolean;
  priority: Task["priority"];
  assigneeHonbuIds: number[];
  requesterHonbuIds: number[];
  tagIds: number[];
  startDateOffset: number | null;
  endDateOffset: number | null;
  childTasks: EditableTemplateChild[];
};

const sanitizeNumberArray = (input: unknown): number[] =>
  Array.isArray(input)
    ? (input as unknown[]).filter((x): x is number => typeof x === "number")
    : [];

export function TemplateEditorPanel({
  template,
  onClose,
  roomMembers,
  availableTags,
  color,
  honbuId,
  roomId,
  onTemplateUpdated,
}: TemplateEditorPanelProps) {
  const isDarkMode = useDarkMode();
  const isMyList = roomId === 0;
  // --- ★フェーズ3 で追加/変更 ---
  const [editableData, setEditableData] = useState<EditableTemplateData | null>(
    null,
  );
  const [selectedTaskKey, setSelectedTaskKey] = useState<string>("parent");
  const [view, setView] = useState<"detail" | "subtaskList">("detail");
  const [localTaskData, setLocalTaskData] = useState<Task | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [memberDialogType, setMemberDialogType] = useState<
    "assignees" | "requesters" | null
  >(null);
  // ------------------------

  // --- ★フェーズ2: データ初期化 ---
  useEffect(() => {
    const raw = JSON.parse(template.taskData) as TemplateTaskDataDto;

    const nameToMemberId = (n: string) =>
      roomMembers.find((m) => m.name === n)
        ? Number(roomMembers.find((m) => m.name === n)!.id)
        : null;
    const nameToTagId = (n: string) =>
      availableTags.find((t) => t.name === n)?.id ?? null;

    const ensureMemberIds = (): number[] =>
      sanitizeNumberArray(
        (raw as any).assigneeHonbuIds ?? (raw as any).assignees,
      );
    const ensureRequesterIds = (): number[] =>
      sanitizeNumberArray(
        (raw as any).requesterHonbuIds ?? (raw as any).requesters,
      );
    const ensureTagIds = (): number[] =>
      sanitizeNumberArray((raw as any).tagIds ?? (raw as any).tags);

    const mapChildren = (
      children: TemplateChildTaskDto[] | undefined,
    ): EditableTemplateChild[] =>
      (children || []).map((c) => ({
        _key: crypto.randomUUID(),
        title: c.title || "",
        memo: c.memo || "",
        assigneeHonbuIds: sanitizeNumberArray(c.assigneeHonbuIds),
        requesterHonbuIds: sanitizeNumberArray(c.requesterHonbuIds),
        tagIds: sanitizeNumberArray(c.tagIds),
        startDateOffset:
          typeof c.startDateOffset === "number" ? c.startDateOffset : null,
        endDateOffset:
          typeof c.endDateOffset === "number" ? c.endDateOffset : null,
        childTasks: mapChildren(c.childTasks),
      }));

    setEditableData({
      title: raw.title || "",
      memo: raw.memo || "",
      notification: raw.notification,
      priority: raw.priority || "なし",
      assigneeHonbuIds: ensureMemberIds(),
      requesterHonbuIds: ensureRequesterIds(),
      tagIds: ensureTagIds(),
      startDateOffset: raw.startDateOffset ?? null,
      endDateOffset: raw.endDateOffset ?? null,
      childTasks: mapChildren(raw.childTasks),
    });
    setSelectedTaskKey("parent");
    setView("detail");
  }, [template.taskData, roomMembers, availableTags]);

  // --- ★フェーズ3: データ変換アダプタ ---

  // ユーティリティ関数
  const mapNamesToUsers = (names: string[]): User[] =>
    names.map((name) => {
      const member = roomMembers.find((m) => m.name === name);
      return member ? member : { id: "0", name: name }; // 見つからない場合はダミー
    });

  const mapIdsToUsers = (ids: number[]): User[] =>
    ids.map((id) => {
      const member = roomMembers.find((m) => Number(m.id) === id);
      return member ? member : { id: id.toString(), name: `ID:${id}` };
    });

  const mapNamesToTags = (names: string[]): Tag[] =>
    names.map((name) => {
      const tag = availableTags.find((t) => t.name === name);
      return tag ? tag : { id: 0, name: name, color: "#ccc" };
    });

  const mapIdsToTags = (ids: number[]): Tag[] =>
    ids.map((id) => {
      const tag = availableTags.find((t) => t.id === id);
      return tag ? tag : { id: id, name: `ID:${id}`, color: "#ccc" };
    });

  const toUsers = (ids: number[]) =>
    ids
      .map((id) => roomMembers.find((m) => Number(m.id) === id))
      .filter((u): u is User => !!u);

  const toTags = (ids: number[]) =>
    ids
      .map((id) => availableTags.find((t) => t.id === id))
      .filter((t): t is Tag => !!t);

  const offsetToIso = (offset: number | null | undefined): string => {
    if (offset == null) return "";
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };

  const isoToOffset = (iso: string): number | null => {
    if (!iso) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(iso + "T00:00:00");
    return Math.round((target.getTime() - today.getTime()) / 86400000);
  };

  const handleOffsetDirect = (type: "start" | "end", val: string) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return;
    setLocalTaskData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };

      // 現在のモード判定
      const isRange = currentIsDateRange;

      if (!isRange) {
        // 単日モード: 入力されたオフセットで両方そろえる
        const iso = offsetToIso(n);
        next.startDate = iso;
        next.endDate = iso;
        return next;
      }
      if (type === "start") {
        next.startDate = offsetToIso(n);
      } else {
        next.endDate = offsetToIso(n);
      }

      // 整合 (開始 > 終了 / 終了 < 開始) を崩さない
      const startOffset = isoToOffset(next.startDate || "");
      const endOffset = isoToOffset(next.endDate || "");

      if (startOffset != null && endOffset != null && startOffset > endOffset) {
        // 開始を動かした結果終了より大きくなった場合は終了を開始に合わせて延ばす (範囲維持)
        if (type === "start") {
          next.endDate = next.startDate;
        } else {
          // 終了側を縮めすぎた場合は開始に合わせる
          next.startDate = next.endDate;
        }
      }
      return next;
    });
    setIsDirty(true);
  };

  // UI表示用のヘルパー: ISO文字列から入力欄に表示する数値を取り出す
  const getOffsetDisplay = (
    iso: string | undefined | null,
  ): string | number => {
    const offset = isoToOffset(iso || "");
    return offset === null ? "" : offset;
  };

  const initialData = JSON.parse(template.taskData) as TemplateTaskDataDto;
  const initialStartDate = offsetToIso(initialData.startDateOffset);
  const initialEndDate = offsetToIso(initialData.endDateOffset);
  const [dateRangeFlags, setDateRangeFlags] = useState<Record<string, boolean>>(
    {
      parent: !!(
        initialStartDate &&
        initialEndDate &&
        initialStartDate !== initialEndDate
      ),
    },
  );

  // 子タスク追加時などで初期化を補完
  useEffect(() => {
    if (!editableData) return;
    setDateRangeFlags((prev) => {
      const next = { ...prev };
      // 親
      next["parent"] = !!(
        editableData.startDateOffset != null &&
        editableData.endDateOffset != null &&
        editableData.startDateOffset !== editableData.endDateOffset
      );
      // 子
      const walk = (arr: EditableTemplateChild[]) => {
        for (const c of arr) {
          next[c._key] = !!(
            c.startDateOffset != null &&
            c.endDateOffset != null &&
            c.startDateOffset !== c.endDateOffset
          );
          walk(c.childTasks);
        }
      };
      walk(editableData.childTasks);
      return next;
    });
  }, [editableData]);

  // 現在選択中タスクの期間フラグ
  const currentIsDateRange = dateRangeFlags[selectedTaskKey] || false;

  // 選択中のタスクを "偽装Task" オブジェクトとして生成
  const currentEditingTask: Task | null = useMemo(() => {
    if (!editableData) return null;
    if (selectedTaskKey === "parent") {
      return {
        id: "parent",
        title: editableData.title,
        memo: editableData.memo || "",
        notification: editableData.notification,
        priority: editableData.priority,
        assignees: toUsers(editableData.assigneeHonbuIds),
        requesters: toUsers(editableData.requesterHonbuIds),
        tags: toTags(editableData.tagIds),
        startDate: offsetToIso(editableData.startDateOffset),
        endDate: offsetToIso(editableData.endDateOffset),
        type: editableData.childTasks.length > 0 ? "multi" : "single",
        status: "未対応",
        completed: false,
        starred: false,
        createdAt: "",
        lastUpdateAt: "",
        reminders: [],
        childTasks: [],
        recurrenceRules: [],
      } as Task;
    }
    const findChild = (
      arr: EditableTemplateChild[],
    ): EditableTemplateChild | null => {
      for (const c of arr) {
        if (c._key === selectedTaskKey) return c;
        const f = findChild(c.childTasks);
        if (f) return f;
      }
      return null;
    };
    const c = findChild(editableData.childTasks);
    if (!c) return null;
    return {
      id: c._key,
      title: c.title,
      memo: c.memo || "",
      notification: false,
      priority: "なし",
      assignees: toUsers(c.assigneeHonbuIds),
      requesters: toUsers(c.requesterHonbuIds),
      tags: toTags(c.tagIds),
      startDate: offsetToIso(c.startDateOffset),
      endDate: offsetToIso(c.endDateOffset),
      type: (c.childTasks || []).length > 0 ? "multi" : "single",
      status: "未対応",
      completed: false,
      starred: false,
      createdAt: "",
      lastUpdateAt: "",
      reminders: [],
      childTasks: [],
      recurrenceRules: [],
    } as Task;
  }, [editableData, selectedTaskKey, roomMembers, availableTags]);

  // --- ★フェーズ3: 自動保存ロジック ---

  // ローカル編集用State
  useEffect(() => {
    if (currentEditingTask) {
      setLocalTaskData(currentEditingTask);
    }
  }, [currentEditingTask]);

  // TaskDetailPanelの handleDetailChange の代わり
  const handleTemplateUpdate = (updates: Partial<Task>) => {
    setLocalTaskData((prev) => {
      if (!prev) return null;
      let next = { ...prev, ...updates };
      if (next.startDate && next.endDate && next.startDate > next.endDate) {
        const temp = next.startDate;
        next.startDate = next.endDate;
        next.endDate = temp;
      }

      return next;
    });
    setIsDirty(true);
  };

  // DBへ保存する関数
  const saveSeqRef = useRef(0);
  const saveChanges = async (newData: EditableTemplateData) => {
    const seq = ++saveSeqRef.current;
    setEditableData(newData);
    setIsDirty(false);
    setIsSaving(true);

    const payload = JSON.stringify(newData, (k, v) =>
      k === "_key" ? undefined : v,
    );
    try {
      const res = await fetch(
        `/api/templates/${template.templateId}/data?honbuId=${honbuId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: payload,
        },
      );
      if (!res.ok) throw new Error(res.status.toString());
      if (seq === saveSeqRef.current) {
        onTemplateUpdated(template.templateId, payload);
        setShowSaveConfirmation(true);
        setTimeout(() => setShowSaveConfirmation(false), 1500);
      }
    } catch (e) {
      console.error("save failed", e);
      setIsDirty(true);
    } finally {
      if (seq === saveSeqRef.current) setIsSaving(false);
    }
  };

  // デバウンス自動保存
  useEffect(() => {
    if (!isDirty || !localTaskData || !editableData) return;
    const key = localTaskData.id;
    const newData: EditableTemplateData = JSON.parse(
      JSON.stringify(editableData),
    );

    const apply = (obj: EditableTemplateChild | EditableTemplateData) => {
      obj.title = localTaskData.title;
      obj.memo = localTaskData.memo || "";
      obj.startDateOffset = isoToOffset(localTaskData.startDate || "") ?? null;
      obj.endDateOffset = isoToOffset(localTaskData.endDate || "") ?? null;
      if (key === "parent") {
        if ("notification" in obj)
          obj.notification = localTaskData.notification;
        if ("priority" in obj) obj.priority = localTaskData.priority;
        (obj as EditableTemplateData).assigneeHonbuIds = (
          localTaskData.assignees || []
        ).map((u) => Number(u.id));
        (obj as EditableTemplateData).requesterHonbuIds = (
          localTaskData.requesters || []
        ).map((u) => Number(u.id));
        (obj as EditableTemplateData).tagIds = (localTaskData.tags || []).map(
          (t) => t.id,
        );
      } else {
        (obj as EditableTemplateChild).assigneeHonbuIds = (
          localTaskData.assignees || []
        ).map((u) => Number(u.id));
        (obj as EditableTemplateChild).requesterHonbuIds = (
          localTaskData.requesters || []
        ).map((u) => Number(u.id));
        (obj as EditableTemplateChild).tagIds = (localTaskData.tags || []).map(
          (t) => t.id,
        );
      }
    };

    if (key === "parent") {
      apply(newData);
    } else {
      const walk = (arr: EditableTemplateChild[]): boolean => {
        for (const c of arr) {
          if (c._key === key) {
            apply(c);
            return true;
          }
          if (walk(c.childTasks)) return true;
        }
        return false;
      };
      walk(newData.childTasks);
    }

    const timer = setTimeout(() => saveChanges(newData), 500);
    return () => clearTimeout(timer);
  }, [localTaskData, isDirty]);

  // --- ★フェーズ4: サブタスク追加 ---
  const handleAddNewSubtask = () => {
    if (!editableData || selectedTaskKey !== "parent") return;
    const newKey = crypto.randomUUID();
    const newSubtask: EditableTemplateChild = {
      _key: newKey,
      title: "新規サブタスク",
      memo: "",
      assigneeHonbuIds: [],
      requesterHonbuIds: [],
      tagIds: [],
      startDateOffset: null,
      endDateOffset: null,
      childTasks: [],
    };
    const newData = {
      ...editableData,
      childTasks: [...editableData.childTasks, newSubtask],
    };
    setView("detail");
    setDateRangeFlags((f) => ({ ...f, [newKey]: false }));
    setIsDirty(true);
    saveChanges(newData);
  };

  // サブタスク一覧表示用のコンポーネント
  const SubtaskListItem = ({
    subtask,
    onClick,
  }: {
    subtask: EditableTemplateChild;
    onClick: () => void;
  }) => {
    return (
      <div
        onClick={onClick}
        className={`p-3 border-b last:border-b-0 cursor-pointer ${
          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="flex-grow font-medium">{subtask.title}</span>
        </div>
        <div className="pl-3 flex flex-wrap items-center text-xs text-gray-500 gap-x-4 gap-y-1 mt-1.5">
          {subtask.assigneeHonbuIds.length > 0 && (
            <span>
              担当:{" "}
              {toUsers(subtask.assigneeHonbuIds)
                .map((u) => u.name)
                .join(", ")}
            </span>
          )}
          {subtask.startDateOffset !== null && (
            <span>期限: 作成日+{subtask.startDateOffset}日後</span>
          )}
        </div>
      </div>
    );
  };

  const handleMemberSave = (selectedNames: string[]) => {
    if (!memberDialogType) return;
    const selectedUsers = selectedNames
      .map((name) => roomMembers.find((member) => member.name === name))
      .filter((user): user is User => !!user);
    const updateProperty =
      memberDialogType === "assignees" ? "assignees" : "requesters";
    handleTemplateUpdate({ [updateProperty]: selectedUsers } as Partial<Task>);
    setMemberDialogType(null);
  };

  const handleTagChange = (tag: Tag) => {
    if (selectedTaskKey !== "parent") return; // 子タスクではタグ編集不可
    if (!localTaskData) return;
    const current = localTaskData.tags || [];
    const exists = current.some((t) => t.id === tag.id);
    const newTags = exists
      ? current.filter((t) => t.id !== tag.id)
      : [...current, tag];
    handleTemplateUpdate({ tags: newTags });
  };

  const getRequesterOrCreatorInfo = useMemo(() => {
    if (!localTaskData)
      return { label: "情報なし", names: [] as string[], isEditable: true };
    const requesterNames = (localTaskData.requesters || []).map((u) => u.name);
    return {
      label: "依頼者",
      names: [requesterNames.join(", ")],
      isEditable: true,
    };
  }, [localTaskData]);

  const switchStyles = {
    "&.Mui-checked": {
      color: PaletteColorHex[color],
      "& + .MuiSwitch-track": { backgroundColor: PaletteColorHex[color] },
    },
  };

  // ---------------------------------------------
  // --- TaskDetailPanel から移植した詳細JSX ---
  // ---------------------------------------------
  const detailContent = (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="space-y-4 overflow-y-auto flex-1 p-6 pr-4">
        <input
          type="text"
          value={localTaskData?.title || ""}
          onChange={(e) => handleTemplateUpdate({ title: e.target.value })}
          className="text-xl font-bold text-gray-800 w-full border-b-2 focus:border-blue-500 outline-none"
        />
        <div className="flex items-center space-x-2">
          {/* 依頼者セクション - マイリストでは非表示 */}
          {!isMyList && (
            <div className="flex items-center space-x-2 min-w-0 flex-grow">
              <span className="text-xs font-bold text-gray-500 flex-shrink-0">
                {getRequesterOrCreatorInfo.label}：
              </span>
              <span className="text-xs truncate min-w-0">
                {getRequesterOrCreatorInfo.names.join(", ") || "ー"}
              </span>
              {getRequesterOrCreatorInfo.isEditable && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberDialogType("requesters");
                  }}
                  className="text-gray-500 hover:text-blue-500 py-1 -m-1"
                  title="依頼者を編集"
                >
                  <PersonAddAlt1OutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* --- 担当者・期限・優先度・タグ・メモ --- */}
        <>
          {/* 担当者セクション - マイリストでは非表示 */}
          {!isMyList && (
            <div>
              <div className="flex items-center text-gray-500 gap-2">
                <PeopleTwoToneIcon fontSize="small" />
                <span className="text-sm font-bold">
                  担当者 ({(localTaskData?.assignees || []).length}名)
                </span>
              </div>
              <div
                className="flex items-center flex-wrap gap-1 mt-2 p-2 border rounded min-h-[40px] cursor-pointer"
                onClick={() => setMemberDialogType("assignees")}
              >
                {(localTaskData?.assignees || []).map((u) => (
                  <span
                    key={u.id}
                    className="text-xs px-2 py-1 bg-gray-200 rounded-full"
                  >
                    {u.name}
                  </span>
                ))}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberDialogType("assignees");
                  }}
                  className="text-sm hover:underline ml-auto"
                  style={{ color: PaletteColorHex[color] }}
                >
                  <PersonAddTwoToneIcon
                    fontSize="small"
                    className="mb-1 mr-1"
                  />
                  追加
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center text-gray-500 gap-2">
              <EventTwoToneIcon fontSize="small" />
              <span className="text-sm font-bold">
                {currentIsDateRange
                  ? "期間 (作成日からの日数)"
                  : "期限 (作成日からの日数)"}
              </span>
            </div>

            <div className="mt-2">
              {/* 未設定表示ボックス */}
              {!localTaskData?.startDate && (
                <div
                  className="flex items-center bg-gray-100 border rounded-md px-3 py-2 mb-2 shadow-sm"
                  onClick={() => {
                    const todayIso = offsetToIso(0);
                    // 選択タスクの期間フラグをOFF
                    setDateRangeFlags((f) => ({
                      ...f,
                      [selectedTaskKey]: false,
                    }));
                    handleTemplateUpdate({
                      startDate: todayIso,
                      endDate: todayIso,
                    });
                  }}
                  title="クリックで今日の日付を設定"
                >
                  未設定
                </div>
              )}

              {/* 設定済みのときの編集UI */}
              {localTaskData?.startDate && (
                <div className="ml-1 mb-2">
                  {!currentIsDateRange && (
                    <div className="flex items-center bg-white border rounded-md px-3 py-2 shadow-sm">
                      <span className="text-sm text-gray-700 mr-2">
                        作成日の
                      </span>
                      <input
                        type="number"
                        min="0"
                        className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                        value={getOffsetDisplay(localTaskData.startDate)}
                        onChange={(e) =>
                          handleOffsetDirect("start", e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                      />
                      <span className="text-sm text-gray-700 ml-2">日後</span>
                    </div>
                  )}
                  {currentIsDateRange && (
                    <div className="flex items-center bg-white border rounded-md px-3 py-2 shadow-sm gap-2 flex-wrap">
                      <span className="text-sm text-gray-700">作成日の</span>
                      <input
                        type="number"
                        min="0"
                        className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                        value={getOffsetDisplay(localTaskData.startDate)}
                        onChange={(e) =>
                          handleOffsetDirect("start", e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                      />
                      <span className="text-sm text-gray-700">～</span>
                      <input
                        type="number"
                        min="0"
                        className="w-16 text-center p-1 border rounded focus:outline-none focus:border-blue-500"
                        value={getOffsetDisplay(localTaskData.endDate)}
                        onChange={(e) =>
                          handleOffsetDirect("end", e.target.value)
                        }
                        onFocus={(e) => e.target.select()}
                      />
                      <span className="text-sm text-gray-700">日後</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-1">
                  <Switch
                    checked={!localTaskData?.startDate} // ← 反転
                    onChange={(_, checked) => {
                      if (checked) {
                        // ON → 未設定へ
                        setDateRangeFlags((f) => ({
                          ...f,
                          [selectedTaskKey]: false,
                        }));
                        handleTemplateUpdate({ startDate: "", endDate: "" });
                      } else {
                        // OFF → 今日を単日設定
                        const todayIso = offsetToIso(0);
                        setDateRangeFlags((f) => ({
                          ...f,
                          [selectedTaskKey]: false,
                        }));
                        handleTemplateUpdate({
                          startDate: todayIso,
                          endDate: todayIso,
                        });
                      }
                    }}
                    size="small"
                    sx={switchStyles}
                  />
                  <span className="text-sm select-none">未設定にする</span>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    checked={currentIsDateRange}
                    disabled={!localTaskData?.startDate}
                    onChange={(_, checked) => {
                      setDateRangeFlags((f) => ({
                        ...f,
                        [selectedTaskKey]: checked,
                      }));
                      if (!checked) {
                        // 単日に戻す
                        handleTemplateUpdate({
                          endDate: localTaskData?.startDate,
                        });
                      } else if (
                        localTaskData?.endDate === localTaskData?.startDate
                      ) {
                        const startOffset =
                          isoToOffset(localTaskData?.startDate || "") || 0;
                        handleTemplateUpdate({
                          endDate: offsetToIso(startOffset + 1),
                        });
                      }
                    }}
                    size="small"
                    sx={switchStyles}
                  />
                  <span className="text-sm select-none">期間を設定</span>
                </div>
              </div>
            </div>
          </div>

          {/* 親タスク選択中のみ表示 */}
          {selectedTaskKey === "parent" && (
            <>
              <div>
                <div className="flex items-center text-gray-500 gap-2">
                  <LabelImportantTwoToneIcon fontSize="small" />
                  <span className="text-sm font-bold">重要度</span>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1 rounded-lg bg-gray-200 p-1">
                  {(["なし", "高", "中", "低"] as const).map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => handleTemplateUpdate({ priority })}
                      className={`rounded-md py-1 text-sm font-semibold transition-colors duration-200 ${
                        localTaskData?.priority === priority
                          ? "bg-white text-gray-800 shadow"
                          : "bg-transparent text-gray-500 hover:bg-white/60"
                      }`}
                    >
                      {priority}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {selectedTaskKey === "parent" && (
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-500 gap-2">
                  <StyleTwoToneIcon fontSize="small" />
                  <span className="text-sm font-bold">タグ</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableTags.map((tag) => {
                  const isSelected = (localTaskData?.tags || []).some(
                    (t) => t.id === tag.id,
                  );
                  const hexColor = tag.color || PaletteColorHex.Blue;
                  return (
                    <div
                      key={tag.id}
                      onClick={() => handleTagChange(tag)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? `${hexColor}20` : "white",
                        color: isSelected ? hexColor : `${hexColor}80`,
                        border: `1px solid ${hexColor}80`,
                      }}
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                    >
                      #{tag.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* リマインダー (削除) */}
          {/* 自動作成 (削除) */}
        </>

        <div>
          <div className="flex items-center text-gray-500 gap-2">
            <NoteAltTwoToneIcon fontSize="small" />
            <span className="text-sm font-bold">メモ</span>
          </div>
          <textarea
            placeholder="メモを入力..."
            className="mt-1 w-full min-h-[120px] border-2 border-gray-200 rounded-lg p-2"
            value={localTaskData?.memo || ""}
            onChange={(e) => handleTemplateUpdate({ memo: e.target.value })}
          />
        </div>

        {/* 親タスク選択中のみ表示
        {selectedTaskKey === "parent" && (
          <FormControlLabel
            control={
              <Switch
                checked={localTaskData?.notification || false}
                onChange={(e) =>
                  handleTemplateUpdate({ notification: e.target.checked })
                }
              />
            }
            label="作成時に通知する"
          />
        )} */}
      </div>

      {/* フッター (保存中インジケータのみ) */}
      <div className="mt-auto pt-4 p-6 border-t h-8 flex items-center justify-center gap-2">
        {isSaving && (
          <div className="flex items-center justify-center text-gray-500">
            <span>保存中...</span>
          </div>
        )}
        {!isSaving && showSaveConfirmation && (
          <div className="flex items-center justify-center text-green-600 animate-fade-in-out">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            <span>保存しました</span>
          </div>
        )}
      </div>
    </div>
  );

  // ---------------------------------------------
  // --- メインコンポーネントのJSX (フェーズ2) ---
  // ---------------------------------------------

  if (!editableData || !localTaskData) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
        <div className="w-full md:w-[768px] h-full bg-white shadow-lg animate-slide-in-right flex items-center justify-center">
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* メンバー選択ダイアログ (TaskDetailPanelから流用) */}
      {memberDialogType && (
        <div className="relative z-[80]">
          <MemberSelectionDialog
            roomMembers={roomMembers}
            initialSelected={(memberDialogType === "assignees"
              ? localTaskData?.assignees || []
              : localTaskData?.requesters || []
            ).map((u) => u.name)}
            onSave={handleMemberSave}
            onClose={() => setMemberDialogType(null)}
            dialogType={memberDialogType}
          />
        </div>
      )}

      {/* --- 編集パネル本体 --- */}
      <div
        className="fixed inset-0 bg-black/50 z-[70] flex justify-end"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <div
          className={`w-full md:w-[768px] h-full shadow-lg animate-slide-in-right flex flex-col ${
            isDarkMode ? "bg-gray-900 text-gray-200" : "bg-white text-gray-900"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* --- ヘッダー --- */}
          <div
            className={`flex-shrink-0 p-4 border-b flex justify-between items-center ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3 className="font-bold">
              テンプレート編集: {template.templateName}
            </h3>
            <button
              onClick={onClose}
              className={`p-2 rounded-full ${
                isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
              }`}
            >
              <CloseIcon />
            </button>
          </div>

          {/* --- メインの編集エリア (左右分割) --- */}
          <div className="flex flex-1 min-h-0 flex-col md:flex-row">
            {/* --- モバイル: 上部に切り替えボタン --- */}
            {editableData.childTasks.length > 0 && (
              <div
                className={`md:hidden flex justify-center py-2 border-b shrink-0 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`inline-flex rounded-lg p-1 ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}
                >
                  <button
                    onClick={() => {
                      setSelectedTaskKey("parent");
                      setView("detail");
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      view === "detail" && selectedTaskKey === "parent"
                        ? isDarkMode
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-900 shadow-sm"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    メインタスク
                  </button>
                  <button
                    onClick={() => {
                      setView("subtaskList");
                      setSelectedTaskKey("parent");
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      view === "subtaskList"
                        ? isDarkMode
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-900 shadow-sm"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    サブタスク ({editableData.childTasks.length})
                  </button>
                </div>
              </div>
            )}

            {/* --- モバイル: サブタスクの詳細表示時に戻るボタン --- */}
            {selectedTaskKey !== "parent" && view === "detail" && (
              <div
                className={`md:hidden flex items-center p-3 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => {
                    setSelectedTaskKey("parent");
                    setView("subtaskList");
                  }}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium ${
                    isDarkMode
                      ? "text-gray-300 hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <ArrowBackIcon fontSize="small" />
                  サブタスク一覧に戻る
                </button>
              </div>
            )}

            {/* --- デスクトップ: 左側の階層ビュー（md以上で表示） --- */}
            <div
              className={`hidden md:flex w-1/3 border-r h-full flex flex-col ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {/* 親タスク */}
                <div
                  onClick={() => {
                    setSelectedTaskKey("parent");
                    setView("detail");
                  }}
                  className={`p-2 rounded cursor-pointer flex items-center gap-2 ${
                    selectedTaskKey === "parent"
                      ? isDarkMode
                        ? "bg-blue-900 font-semibold"
                        : "bg-blue-100 font-semibold"
                      : isDarkMode
                        ? "hover:bg-gray-700"
                        : "hover:bg-gray-100"
                  }`}
                >
                  <AssignmentTurnedInTwoToneIcon
                    fontSize="small"
                    className="text-gray-600"
                  />
                  <span className="truncate">
                    {editableData?.title || "(親タスク)"}
                  </span>
                </div>

                {/* サブタスク一覧 */}
                <div className="space-y-1">
                  {editableData?.childTasks.map((child) => (
                    <div
                      key={child._key}
                      onClick={() => {
                        setSelectedTaskKey(child._key);
                        setView("detail");
                      }}
                      className={`p-2 pl-6 rounded cursor-pointer text-sm flex items-center gap-1 ${
                        selectedTaskKey === child._key
                          ? isDarkMode
                            ? "bg-blue-900 font-semibold"
                            : "bg-blue-100 font-semibold"
                          : isDarkMode
                            ? "hover:bg-gray-700"
                            : "hover:bg-gray-100"
                      }`}
                    >
                      <SubdirectoryArrowRightTwoToneIcon
                        fontSize="small"
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      />
                      <span className="truncate">
                        {child.title || "新規サブタスク"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* --- ④ サブタスク追加ボタン (デスクトップ) --- */}
              {selectedTaskKey === "parent" && (
                <div
                  className={`p-2 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <button
                    onClick={handleAddNewSubtask}
                    className="w-full px-4 py-2 text-sm text-white rounded hover:brightness-105"
                    style={{
                      backgroundColor: isDarkMode
                        ? PaletteColorDarkHex[color]
                        : PaletteColorHex[color],
                    }}
                  >
                    <AddIcon fontSize="small" className="mr-1" />
                    サブタスクを追加
                  </button>
                </div>
              )}
            </div>

            {/* --- 右側：詳細コンテンツまたはサブタスク一覧 --- */}
            <div className="w-full md:w-2/3 h-full flex flex-col">
              {view === "detail" ? (
                detailContent
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {editableData.childTasks.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      サブタスクはまだありません
                    </div>
                  ) : (
                    editableData.childTasks.map((child) => (
                      <SubtaskListItem
                        key={child._key}
                        subtask={child}
                        onClick={() => {
                          setSelectedTaskKey(child._key);
                          setView("detail");
                        }}
                      />
                    ))
                  )}
                  <div className="p-4 border-t">
                    <button
                      onClick={handleAddNewSubtask}
                      className={`w-full py-2 px-4 rounded-md text-sm font-medium border-2 border-dashed transition-colors ${
                        isDarkMode
                          ? "border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300 hover:bg-gray-800"
                          : "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      + サブタスクを追加
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
